import { inject } from 'tsyringe'
import Vue from 'vue'
import { ClientOnly } from '~/decorators'
import { injectable } from '~/decorators/dependency-container'
import { DestroyListenersFunction } from '~/models/event/builder'
import { IEventBuilder } from '~/builders/event/IEventBuilder'
import EventBuilderValidator from '~/builders/event/EventBuilderValidator'

@ClientOnly
@injectable()
export default class WidthResizeEventBuilder implements IEventBuilder {
  private componentInstance: Vue | undefined
  private breakpointWidth: number | undefined
  private aboveBreakpointWidth: boolean | undefined
  private upHandler: (() => void) | undefined
  private downHandler: (() => void) | undefined

  constructor(
    @inject(EventBuilderValidator)
    private eventBuilderValidator: EventBuilderValidator
  ) {}

  setComponent(componentInstance: Vue): WidthResizeEventBuilder {
    this.componentInstance = componentInstance
    return this
  }

  setBreakpoint(breakpointWidth: number): WidthResizeEventBuilder {
    this.breakpointWidth = breakpointWidth
    return this
  }

  onUp(upHandler: () => void): WidthResizeEventBuilder {
    this.upHandler = upHandler
    return this
  }

  onDown(downHandler: () => void): WidthResizeEventBuilder {
    this.downHandler = downHandler
    return this
  }

  build(): DestroyListenersFunction {
    this.validateBuildPrerequisites()

    const listener = () => {
      const windowWidth = window.innerWidth
      const bw = this.breakpointWidth!
      const above = this.aboveBreakpointWidth

      if (windowWidth < bw && (above || typeof above === 'undefined')) {
        this.aboveBreakpointWidth = false
        this.downHandler!()
      } else if (
        windowWidth >= bw &&
        (!above || typeof above === 'undefined')
      ) {
        this.aboveBreakpointWidth = true
        this.upHandler!()
      }
    }

    listener()
    window.addEventListener('resize', listener)

    const removeListener = () => {
      window.removeEventListener('resize', listener)
    }

    this.componentInstance!.$once('hook:beforeDestroy', removeListener)

    return removeListener
  }

  private validateBuildPrerequisites() {
    const { eventBuilderValidator: v } = this
    v.validate(this.componentInstance, 'component')
    v.validate(this.breakpointWidth, 'breakpoint')
    v.validate(
      this.upHandler && this.downHandler,
      'handlers: at least one required'
    )
  }
}
